import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Cart-drawer / cart-page mini upsell card.
 *
 * Survives morph re-renders because the click is bound declaratively via
 * `on:click="/addToCart"` and dispatched by Horizon's document-level
 * delegated listener (see assets/component.js > registerEventListeners).
 *
 * The companion inline <script> approach failed because morphdom does not
 * re-execute injected <script> tags, so the IIFE never bound on cart-drawer
 * morph or on iOS Safari's first drawer open.
 */
class EpxUpsellComponent extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /**
   * @param {MouseEvent} event
   */
  addToCart = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const button = this.querySelector('[data-epx-upsell-add]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const variantId = this.dataset.variantId;
    if (!variantId) return;

    if (this.#activeFetch) this.#activeFetch.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    const originalLabel = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';

    // Ask Shopify to re-render every cart section currently on the page so
    // the cart-items list, totals, header bubble, and our own upsell snippet
    // all morph in lockstep.
    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      const id = /** @type {HTMLElement} */ (el).dataset.sectionId;
      if (id) sectionIds.add(id);
    });
    document.querySelectorAll('cart-drawer-component, cart-icon-component, [data-section-id]').forEach((el) => {
      const id = /** @type {HTMLElement} */ (el).dataset.sectionId;
      if (id) sectionIds.add(id);
    });

    const body = {
      items: [{ id: parseInt(variantId, 10), quantity: 1 }],
    };
    if (sectionIds.size > 0) {
      body.sections = Array.from(sectionIds).join(',');
      body.sections_url = window.location.pathname;
    }

    try {
      const config = fetchConfig('json', { body: JSON.stringify(body) });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Add to cart failed: ${response.status}`);
      }

      const data = await response.json();

      button.textContent = 'Added ✓';
      button.dataset.added = '1';

      // Fire the canonical event. cart-items-component listens and morphs
      // every cart section using the sections payload we just received.
      // cart-drawer-component also listens and will auto-open if it has
      // the `auto-open` attribute set.
      document.dispatchEvent(
        new CartAddEvent(data, this.id || 'epx-upsell', {
          source: 'epx-upsell',
          variantId,
          itemCount: data.item_count,
          sections: data.sections,
        })
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('[epx-upsell] add to cart failed', error);
      button.textContent = originalLabel || '+ Add';
      button.disabled = false;
    } finally {
      this.#activeFetch = null;
    }
  };
}

if (!customElements.get('epx-upsell-component')) {
  customElements.define('epx-upsell-component', EpxUpsellComponent);
}
